import polyglotI18nProvider from 'ra-i18n-polyglot';
import englishMessages from 'ra-language-english';
import type { TranslationMessages } from 'react-admin';
import slovakMessages from 'ra-language-slovak';

/**
 * Slovak, with English underneath.
 *
 * ra-language-slovak is built for react-admin v3 and this is v5, so it is
 * missing keys that have been added since. Layering it over the English
 * defaults means an untranslated key falls back to English rather than
 * rendering as `ra.action.something`, which is what polyglot does with a
 * missing key and is far worse than an English word.
 *
 * Everything below `ra.` is this application's own copy.
 */
function deepMerge<T extends Record<string, unknown>>(base: T, ...layers: Record<string, unknown>[]): T {
  const result: Record<string, unknown> = { ...base };

  for (const layer of layers) {
    for (const [key, value] of Object.entries(layer)) {
      const existing = result[key];
      result[key] =
        isRecord(existing) && isRecord(value) ? deepMerge(existing, value) : value;
    }
  }

  return result as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const messages = deepMerge(
  englishMessages as unknown as Record<string, unknown>,
  slovakMessages as unknown as Record<string, unknown>,
  {
    ra: {
      action: {
        // v5 keys ra-language-slovak predates.
        select_columns: 'Stĺpce',
        update: 'Upraviť',
        move_up: 'Posunúť vyššie',
        move_down: 'Posunúť nižšie',
        clear_array_input: 'Vyprázdniť zoznam',
        select_all_button: 'Vybrať všetko'
      },
      page: { access_denied: 'Prístup zamietnutý', authentication_error: 'Chyba prihlásenia' },
      message: {
        access_denied: 'Na túto stránku nemáte oprávnenie.',
        authentication_error: 'Prihlásenie zlyhalo.',
        select_all_limit_reached: 'Položiek je príliš veľa na hromadný výber.'
      }
    },
    resources: {
      orders: { name: 'Objednávka |||| Objednávky' },
      products: { name: 'Produkt |||| Produkty' },
      users: { name: 'Používateľ |||| Používatelia' },
      groups: { name: 'Skupina |||| Skupiny' },
      organizations: { name: 'Organizácia |||| Organizácie' },
      loyalty: { name: 'Odmena |||| Vernostný program' },
      content: { name: 'Stránka |||| Obsah stránok' },
      staff: { name: 'Kontakt |||| Kontakty' },
      offers: { name: 'Akcia |||| Akcie a bannery' },
      producers: { name: 'Výrobca |||| Výrobcovia' },
      admins: { name: 'Prístup |||| Prístupy do administrácie' }
    },
    app: {
      order: {
        number: 'Číslo objednávky',
        placed: 'Objednané',
        customer: 'Zákazník',
        organisation: 'Organizácia',
        invoiceSent: 'Faktúra odoslaná',
        fulfilled: 'Vybavená',
        open: 'Otvorená',
        markFulfilled: 'Označiť ako vybavenú',
        showFulfilled: 'Zobraziť vybavené',
        searchHint: 'Číslo objednávky alebo zákazník',
        items: 'Položky',
        empties: 'Vrátené prázdne',
        noItems: 'Objednávka neobsahuje položky.',
        noEmpties: 'Žiadne vrátené prázdne kusy.',
        invoiceAddress: 'Fakturačná adresa',
        deliveryAddress: 'Dodacia adresa',
        notRecorded: 'Neuvedené',
        fulfilledNotice: 'Objednávka označená ako vybavená.',
        fulfilledFailed: 'Objednávku sa nepodarilo označiť — možno vás niekto predbehol.',
        totalsMissing:
          'Sumy objednávok nie sú uložené. Stĺpec item_price je prázdny pri každej položke, takže to, koľko zákazník zaplatil, v tejto databáze nie je — pri objednávkach cez qzila je to v tej službe.'
      },
      user: {
        name: 'Meno',
        group: 'Skupina',
        points: 'Body',
        account: 'Účet',
        lastLogin: 'Posledné prihlásenie',
        never: 'Nikdy',
        locked: 'Zablokovaný',
        disabled: 'Neaktívny',
        unverified: 'Neoverený',
        active: 'Aktívny',
        noGroup: 'Bez skupiny — platí štandardnú cenu',
        searchHint: 'E-mail alebo meno',
        orders: 'Objednávky',
        noOrders: 'Žiadne objednávky.',
        groupChanged: 'Skupina zmenená. Zmenili sa tým všetky ceny, ktoré tento používateľ vidí.',
        groupChangeFailed: 'Skupinu sa nepodarilo zmeniť.',
        discountNote: 'Zľavy platia len na náplne; tlačiarne sa nezľavňujú.'
      },
      product: {
        code: 'Kód',
        name: 'Názov',
        type: 'Typ',
        availability: 'Dostupnosť',
        points: 'Body',
        original: 'Originál',
        renewed: 'Renovovaný',
        searchHint: 'Kód, tag alebo názov',
        producerFilter: 'Výrobca',
        assigned: 'Priradený',
        unassigned: 'Nepriradený',
        originalOrRenewed: 'Originál / renovovaný',
        noImages: 'Bez obrázkov.',
        standardPrice: 'Štandardná cena',
        minimalPrice: 'Minimálna cena',
        buyoutPrice: 'Výkupná cena',
        groupPrices: 'Ceny podľa skupiny',
        explicit: 'pevná cena',
        viaDiscount: 'cez zľavu',
        salePrices: 'Akciové ceny',
        noSales: 'Žiadne akcie.',
        saleActive: 'Prebieha',
        saleEnded: 'Ukončená',
        readOnlyPrices:
          'Ceny sú v tejto verzii len na čítanie. Zápis v API existuje a je pokrytý testami, ale úprava cien si zaslúži samostatné schválenie.',
        discountNote:
          'Skupinová zľava platí len na náplne — tlačiarne ju nikdy nedostanú. Pevná cena nižšie nahrádza percentuálnu zľavu úplne.'
      },
      classification: {
        producer: 'Výrobca',
        categories: 'Kategórie',
        printers: 'Tlačiarne',
        buyout: 'Výkupné produkty',
        add: 'Pridať',
        none: 'Nič nie je priradené.',
        producerHint: 'Určuje filter „výrobca“ v zozname — produkt bez výrobcu sa v ňom nezobrazí.',
        printersHint: 'Tlačiarne, do ktorých tento produkt patrí.',
        buyoutHint: 'Prázdne kusy, ktoré sa vracajú. Výkupná cena je na karte Ceny.'
      },
      group: {
        originalDiscount: 'Zľava na originál %',
        renewalDiscount: 'Zľava na renováciu %',
        members: 'Používatelia',
        locked: 'Názov tejto skupiny sa nedá zmeniť: e-shop ju hľadá podľa názvu pri akciových cenách aj pri objednávkovej obrazovke.'
      },
      organisation: {
        people: 'Používatelia',
        orders: 'Objednávky',
        noPeople: 'K účtu tejto organizácie nie je priradený nikto.',
        noOrders: 'Žiadne objednávky.',
        searchHint: 'Názov, skratka alebo IČO',
        verified: 'Overená',
        relationNote:
          'Používatelia sú priradení cez spoločný účet, objednávky sa viažu priamo na organizáciu. Organizácia teda môže mať objednávky bez používateľov aj naopak.'
      },
      loyalty: {
        reward: 'Odmena',
        points: 'Body',
        order: 'Poradie',
        description: 'Popis',
        pointsHint: 'Voľný text, nie číslo — niektoré odmeny majú „1000 a viac“.',
        orderHint: 'Nastavuje sa ťahaním v zozname.',
        dragToOrder: 'Poradie zmeníte ťahaním. Uloží sa automaticky po pustení.',
        noOrderYet:
          'Ťahaním nastavíte poradie, v ktorom odmeny uvidia zákazníci. Zatiaľ majú všetky rovnakú váhu, takže poradie ešte nie je určené.',
        orderSaved: 'Poradie uložené.',
        orderFailed: 'Poradie sa nepodarilo uložiť.'
      },
      common: {
        showingRecent: 'Zobrazených %{shown} najnovších z %{total}.',
        loading: 'Načítava sa…',
        none: '—'
      }
    }
  }
);

export const i18nProvider = polyglotI18nProvider(
  () => messages as unknown as TranslationMessages,
  'sk',
  [{ locale: 'sk', name: 'Slovenčina' }]
);
